import React, { useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';

import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import MoneyIcon from '@material-ui/icons/Money';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import { PaymentType } from '../../../../../shared/enums/payment-type.enum';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { BookingRequestUiModel } from '../../../../../shared/ui-models/booking-request.ui-model';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import { getPaymentStatus, getPaymentStatusColor } from '../../reservation-edit.helper';
import { ChangeDatesModal } from '../change-dates-modal/change-dates-modal.component';
import { updateReservationDates } from '../../../../shared/services/data.service';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { ChevronRightIcon, Edit, Mail, Phone, User } from '../../../../../ui/Icons';

export interface DatesSummaryBarProps {
  data: {
    reservation: ReservationDetailsUiModel;
    contactInfo?: BookingRequestUiModel;
  };
  eventHandlers: {
    onRefresh: (id: string) => Promise<any>;
  };
}

export const DatesSummaryBar = ({
  data: { reservation, contactInfo },
  eventHandlers: { onRefresh }
}: DatesSummaryBarProps) => {
  const [showChangeDatesModal, setShowChangeDatesModal] = useState<boolean>(false);
  const [changeDatesLoading, setChangeDatesLoading] = useState<boolean>(false);
  const [changeDatesError, setChangeDatesError] = useState<string | undefined>(undefined);

  const [emailCopied, setEmailCopied] = useState<boolean>(false);
  const [idCopied, setIdCopied] = useState<boolean>(false);

  const copyEmail = () => {
    if (contactInfo?.guestInfo?.email) {
      navigator.clipboard.writeText(contactInfo.guestInfo.email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    }
  };

  const copyId = () => {
    if (reservation.userId) {
      navigator.clipboard.writeText(reservation.userId);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    }
  };

  const openModal = () => {
    setChangeDatesError(undefined);
    setShowChangeDatesModal(true);
  };

  const closeModal = () => {
    setShowChangeDatesModal(false);
    setChangeDatesError(undefined);
  };

  const submitHandler = (newCheckInDate: number, newCheckOutDate: number) => {
    setChangeDatesLoading(true);
    setChangeDatesError(undefined);
    updateReservationDates(reservation.id, {
      checkInDate: newCheckInDate,
      checkOutDate: newCheckOutDate
    }).then((response) => {
      setChangeDatesLoading(false);
      if (isErrorInfo(response)) {
        setChangeDatesError(
          response.errorMessage ||
            'The selected dates are not available. Please choose different dates.'
        );
      } else {
        setShowChangeDatesModal(false);
        setChangeDatesError(undefined);
        onRefresh(reservation.id);
      }
    });
  };

  return (
    <div className="flex flex-row flex-wrap items-stretch gap-4">
      <div className="bg-white shadow-sm px-6 pt-12 pb-5 md:my-[1rem] md:mb-[2rem] flex flex-wrap flex-row justify-between  items-center rounded-lg gap-4 relative bg-gradient-to-b from-primary-50/60 to-white self-stretch border border-gray-200 w-full md:w-fit mx-4 md:mx-0">
        <div>
          <div className="mb-2 font-gilroy-medium text-neutral-500 font-medium text-xl uppercase ">
            Check in
          </div>
          <div className="font-gilroy-bold text-xl text-primary-600">
            {new Date(reservation.checkInDate).toDateString()}
          </div>
        </div>
        <ChevronRightIcon className="size-5" fontSize="small" />

        <div>
          <div className="mb-2 font-gilroy-medium text-neutral-500 font-medium text-xl uppercase ">
            Check out
          </div>
          <div className="font-gilroy-bold text-xl text-primary-600">
            {new Date(reservation.checkOutDate).toDateString()}
          </div>
        </div>
        <div className="absolute right-0 top-2 flex items-center justify-between w-full px-2">
          <div className="inline-flex items-center px-3 py-1 rounded-lg bg-primary-600 text-white font-semibold text-base shadow-sm">
            {moment(Number(reservation.checkOutDate)).diff(
              moment(Number(reservation.checkInDate)),
              'days'
            ) + 1}{' '}
            Days
          </div>
          {reservation.source && reservation.source !== ReservationSource.CHANNEL_MANAGER ? (
            <Tooltip title="Edit dates">
              <button
                type="button"
                aria-label="edit dates"
                className="grid size-9 place-items-center rounded-full border border-primary-100 bg-white/70 text-primary-700 shadow-sm transition hover:bg-primary-100 hover:text-white hover:border-primary-600 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                onClick={openModal}
              >
                <Edit size="16px" />
              </button>
            </Tooltip>
          ) : (
            <div />
          )}
        </div>
      </div>

      {/* Payment summary card */}
      <div className="bg-white shadow-sm px-6 pt-12 pb-5 md:my-4 md:mb-8 flex flex-wrap flex-row justify-between items-center rounded-lg gap-8 bg-gradient-to-b from-primary-50/60 to-white self-stretch border border-gray-200 w-full md:w-auto mx-4 md:mx-0">
        <div>
          <div className="mb-2 font-gilroy-medium text-neutral-500 font-medium text-xl uppercase">
            Preferred Method
          </div>
          <div
            className={`font-gilroy-bold text-xl flex items-center gap-2 ${
              reservation.paymentMethod ? 'text-primary-600' : 'text-red-600'
            }`}
          >
            {reservation.paymentMethod ? (
              <>
                {reservation.paymentMethod === PaymentType.BANK_TRANSFER ? (
                  <AccountBalanceIcon fontSize="small" />
                ) : reservation.paymentMethod === PaymentType.CASH ? (
                  <MoneyIcon fontSize="small" />
                ) : (
                  <CreditCardIcon fontSize="small" />
                )}
                {reservation.paymentMethod}
              </>
            ) : (
              'NOT SET'
            )}
          </div>
        </div>

        <div>
          <div className="mb-2 font-gilroy-medium text-neutral-500 font-medium text-xl uppercase">
            Payment Status
          </div>
          <div
            className="font-gilroy-bold text-xl"
            style={{
              color: getPaymentStatusColor(getPaymentStatus(reservation.paymentList || [])).color
            }}
          >
            {getPaymentStatus(reservation.paymentList || []) === PaymentStatus.COLLECTED
              ? 'On Time'
              : PaymentStatus.OVERDUE}
          </div>
        </div>

        <div>
          <div className="mb-2 font-gilroy-medium text-neutral-500 font-medium text-xl uppercase">
            Payment Overview
          </div>
          <div className="font-gilroy-bold text-xl text-primary-600 flex items-center gap-1">
            {reservation.paymentList && (
              <NumberFormat
                value={reservation.paymentList
                  .filter((payment) => payment.status === PaymentStatus.COLLECTED)
                  .reduce(
                    (prev, current) =>
                      current.direction === 'out'
                        ? prev - ((current && current.paymentAmount) || 0)
                        : prev +
                          ((current &&
                            (current.currency === 'usd'
                              ? current.paymentAmount * (reservation.usdRate || 1)
                              : current.paymentAmount)) ||
                            0),
                    0
                  )}
                displayType={'text'}
                thousandSeparator={true}
                prefix="EGP "
              />
            )}
            <span className="text-neutral-400">/</span>
            {reservation.paymentList && (
              <NumberFormat
                value={reservation.paymentList.reduce(
                  (prev, current) =>
                    current.direction === 'out'
                      ? prev - ((current && current.paymentAmount) || 0)
                      : prev +
                        ((current &&
                          (current.currency === 'usd'
                            ? current.paymentAmount * (reservation.usdRate || 1)
                            : current.paymentAmount)) ||
                          0),
                  0
                )}
                displayType={'text'}
                thousandSeparator={true}
                prefix="EGP "
              />
            )}
          </div>
        </div>

        <div>
          <div className="mb-2 font-gilroy-medium text-neutral-500 font-medium text-xl uppercase">
            Late Payments
          </div>
          <div className="font-gilroy-bold text-xl">
            {getPaymentStatus(reservation.paymentList || []) === PaymentStatus.COLLECTED ? (
              <span className="text-primary-600">-</span>
            ) : (
              reservation.paymentList && (
                <NumberFormat
                  value={reservation.paymentList
                    .filter((payment) => payment.status === PaymentStatus.OVERDUE)
                    .reduce((prev, current) => prev + ((current && current.paymentAmount) || 0), 0)}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix="EGP "
                  className="text-red-600"
                />
              )
            )}
          </div>
        </div>
      </div>
      {/* contact info card */}
      {(contactInfo?.guestInfo?.name ||
        contactInfo?.guestInfo?.phoneNumber ||
        contactInfo?.guestInfo?.email) && (
        <div className="bg-white shadow-sm px-6 pt-10 pb-5 md:my-4 md:mb-8 flex flex-col justify-center rounded-lg gap-y-4 bg-gradient-to-b from-primary-50/60 to-white relative border border-gray-200 w-full md:w-fit mx-4 md:mx-0">
          <div className="flex flex-row flex-wrap gap-x-6 gap-y-3">
            {contactInfo?.guestInfo?.name && (
              <Tooltip
                title={
                  idCopied
                    ? 'ID Copied!'
                    : reservation.userId
                    ? `Click to copy ID: ${reservation.userId}`
                    : ''
                }
              >
                <button
                  type="button"
                  onClick={copyId}
                  className="flex items-center gap-1.5 hover:text-primary-800 transition-colors"
                >
                  <User size="14px" className="text-primary-400 shrink-0" />
                  <span className="font-gilroy-medium text-neutral-500 font-medium text-xl">
                    {contactInfo.guestInfo.name}
                  </span>
                </button>
              </Tooltip>
            )}

            {contactInfo?.guestInfo?.phoneNumber && (
              <div className="flex items-center gap-1.5">
                <Phone size="14px" className="text-primary-400 shrink-0" />
                <span className="font-gilroy-medium text-xl text-neutral-500">
                  {contactInfo.guestInfo.phoneNumber}
                </span>
              </div>
            )}
          </div>
          {contactInfo?.guestInfo?.email && (
            <Tooltip title={emailCopied ? 'Copied!' : 'Click to copy email'}>
              <button
                type="button"
                onClick={copyEmail}
                className="flex items-center gap-1.5 font-gilroy-medium text-xl text-neutral-500 hover:text-neutral-800 transition-colors"
              >
                <Mail size="14px" className="text-primary-400 shrink-0" />
                {contactInfo.guestInfo.email}
              </button>
            </Tooltip>
          )}
        </div>
      )}

      <ChangeDatesModal
        config={{ showModal: showChangeDatesModal }}
        data={{
          reservation,
          isLoading: changeDatesLoading,
          errorMessage: changeDatesError
        }}
        eventHandlers={{
          closeHandler: closeModal,
          submitHandler
        }}
      />
    </div>
  );
};
